/* eslint-disable no-console */
import React, { useEffect } from 'react';
import { useGlobal } from '@rcg/ui-react';
import { Typography, Paper } from '@material-ui/core';
import { useReactiveVar } from '@apollo/client';
import getBrand from '../../utils/getBrand';
import { ReactComponent as RigntArrow } from '../../assets/svg/single-arrow.svg';
import { bookingProfileVar } from '../../operations/localState/reactiveVariables';
import { formatDateRange } from '../../utils/common';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../utils/dictionary';
import { timeWithLocale } from '../../utils/timeFormats';
import useStyles from './index.style';

const ItineraryDetails = ({ stretchLayout, sailing, flightProfile }) => {
  const classes = useStyles();

  const {
    data: { localizedDayjs },
  } = useGlobal();
  const getDictionary = useDictionary(Dictionary);

  const bookingProfile = bookingProfileVar();

  const currentBooking = bookingProfile?.currentBooking;
  const bookingFlightProfile = flightProfile || bookingProfile?.flightProfile;
  const guests = bookingFlightProfile?.guests || [];
  const adultGuests = guests.filter((guest) => guest.flightPassengerType === 'ADULT');
  const childGuests = guests.filter((guest) => guest.flightPassengerType !== 'ADULT');

  const bookingSailing = sailing || currentBooking?.sailing || {};
  const itinerary = bookingSailing?.itinerary;
  const cruiseStartDate = localizedDayjs(bookingSailing?.sailDate);
  const cruiseEndDate = cruiseStartDate.add(itinerary?.totalNights, 'day').format('YYYY-MM-DD');

  const bookingProfileVarSubscription = useReactiveVar(bookingProfileVar);
  useEffect(() => {
    // subscribe to reactiveVar to update default values whenever data be updated
  }, [bookingProfileVarSubscription]);

  const pluralizeChildrenLabel =
    childGuests?.length > 1
      ? getDictionary(Dictionary.airItineraryDetailsChildrenLabel.key)
      : getDictionary(Dictionary.airItineraryDetailsChildLabel.key);

  const { startDate, endDate } = formatDateRange(
    bookingSailing?.sailDate,
    cruiseEndDate,
    localizedDayjs,
  );

  const getItineraryName = () => {
    const { name, totalNights } = itinerary || {};
    return `${totalNights} ${getDictionary(Dictionary.itineraryBookingAmendNights.key)} ${name}`;
  };

  const isCelebrity = getBrand()?.id === 'cel';

  function capitalizeWords(text) {
    if (typeof text !== 'string') {
      return '';
    }
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  const itineraryDescription = getItineraryName();
  const shipName = isCelebrity ? capitalizeWords(itinerary?.ship?.name) : itinerary?.ship?.name;
  const departurePortName = `${itinerary?.departurePort?.name}, ${itinerary?.departurePort?.region}`;
  const departureSailTime = timeWithLocale(
    localizedDayjs,
    bookingSailing?.sailTime || bookingProfile?.flightProfile?.sailing?.sailTime,
  );
  if (!bookingSailing) return null;

  const itineraryItems = (
    <div className="items">
      <div>
        <span className="label">{getDictionary(Dictionary.airItineraryDetailsPortLabel.key)}</span>
        <span>{departurePortName}</span>
      </div>
      <div>
        <span className="label">{getDictionary(Dictionary.airItineraryDetailsShipLabel.key)}</span>
        <span>{shipName}</span>
      </div>
      <div className="cruise-dates">
        <span className="label">{getDictionary(Dictionary.airItineraryDetailsDatesLabel.key)}</span>
        <span>
          {startDate} <RigntArrow /> {endDate}
        </span>
      </div>
      <div>
        <span className="label">{getDictionary(Dictionary.airItineraryDetailsTimeLabel.key)}</span>
        <span>{departureSailTime}</span>
      </div>
      {adultGuests?.length > 0 && (
        <div>
          <span className="label">
            {getDictionary(Dictionary.airItineraryDetailsAdultsLabel.key)}
          </span>
          <span>{adultGuests?.length} </span>
        </div>
      )}
      {childGuests?.length > 0 && (
        <div>
          <span className="label">{pluralizeChildrenLabel}</span>
          <span>{childGuests?.length}</span>
        </div>
      )}
      {bookingFlightProfile?.id && (
        <div>
          <span className="label">
            {getDictionary(Dictionary.airItineraryDetailsReservationLabel.key)}
          </span>
          <span>{bookingFlightProfile ? +bookingFlightProfile.id : ''}</span>
        </div>
      )}
    </div>
  );

  return (
    <Paper>
      <div className={`${classes.itineraryroot} ${stretchLayout ? classes.stretch : ''}`}>
        <Typography variant="subtitle1">{itineraryDescription}</Typography>
        <div className="itinerary-items">{itineraryItems}</div>
      </div>
    </Paper>
  );
};

export default ItineraryDetails;
